import React, { useCallback, useContext } from 'react'

import {
  Button, Chat2, FavoriteFilled, FavoriteOutlined, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openCommentsModal, openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import useModelLike from '../../Hooks/useModelLike'

// Like and comment actions for the opened model, rendered for every model (the
// viewer's own and community ones alike). The seed counts ride on openedModel
// (the community serializers expose likeCount and commentsCount); the viewer's
// own like state is user-specific and fetched client-side by useModelLike.
function EngagementActions() {
  const t = useTranslation()
  const { userLoggedIn, pageConfigLoaded } = useContext(ThreeDStudioPageContext)
  const slug = useThreeDStudioStore(state => state.ui?.openedModel?.slug)
  const initialCount = useThreeDStudioStore(state => state.ui?.openedModel?.likeCount) ?? 0
  const commentsCount = useThreeDStudioStore(state => state.ui?.openedModel?.commentsCount) ?? 0

  const {
    isLiked, likeCount, isToggling, toggleLike,
  } = useModelLike({ slug, initialCount, dailyLimitMessage: t('like.dailyLimitReached') })

  const handleLike = useCallback(() => {
    // On this fast page the auth state is unknown until /load-services.js
    // resolves, and `userLoggedIn` is false during that window. Ignore the click
    // until it has loaded so a signed-in user isn't wrongly prompted to sign in.
    if (!pageConfigLoaded) return

    // POST/DELETE answer 401 for anonymous visitors, so prompt instead of firing.
    if (!userLoggedIn) {
      openSignInModal()

      return
    }

    toggleLike()
  }, [pageConfigLoaded, userLoggedIn, toggleLike])

  const handleComments = useCallback(() => {
    openCommentsModal()
  }, [])

  if (!slug) {
    return null
  }

  return (
    <div className="footer-actions__engagement">
      <Button
        icon={<Icon svgComponent={isLiked ? FavoriteFilled : FavoriteOutlined} size={iconSize} notSpaced />}
        type="secondary"
        size="small"
        variant="with-icon"
        actionType="button"
        aria-label={isLiked ? t('like.unlike') : t('like.like')}
        aria-pressed={isLiked}
        onClick={handleLike}
        disabled={isToggling}
      >
        {likeCount}
      </Button>
      <Button
        icon={<Icon svgComponent={Chat2} size={iconSize} notSpaced />}
        type="secondary"
        size="small"
        variant="with-icon"
        aria-label={t('commentsModal.commentsCount', { count: commentsCount })}
        onClick={handleComments}
      >
        {commentsCount}
      </Button>
    </div>
  )
}

export default EngagementActions
